/**
 * Product gallery thumbnail row.
 *
 * The thumbnails are one sideways-scrolling row (see .pdp-thumbnail-grid in
 * src/theme.css). This adds what a bare scroller lacks:
 *   - prev/next arrows, shown only while there is more in that direction
 *   - a frame on the thumbnail whose image is in the big picture, and the row
 *     scrolls to keep it in view when a colour/variant swaps the picture
 * The big image is swapped by cs-product.js (thumbnail click, variant change);
 * this only watches its src, so the two files stay independent.
 */
(function () {
  const wrap = document.querySelector('[data-cs-thumbs]');
  if (!wrap) return;
  const track = wrap.querySelector('[data-cs-thumbs-track]');
  const prev = wrap.querySelector('[data-cs-thumbs-prev]');
  const next = wrap.querySelector('[data-cs-thumbs-next]');
  const main = document.querySelector('[data-cs-main-image]');
  const thumbs = Array.from(track.querySelectorAll('[data-cs-thumb]'));

  const updateArrows = () => {
    const max = track.scrollWidth - track.clientWidth;
    prev.hidden = track.scrollLeft <= 2;
    next.hidden = track.scrollLeft >= max - 2;
  };

  const page = (dir) => {
    track.scrollBy({ left: dir * track.clientWidth * 0.9, behavior: 'smooth' });
  };
  prev.addEventListener('click', () => page(-1));
  next.addEventListener('click', () => page(1));
  track.addEventListener('scroll', updateArrows, { passive: true });
  window.addEventListener('resize', updateArrows);

  // Same file, different URL shape (thumbnail data-full vs variant image).
  const file = (url) => {
    const m = (url || '').match(/\/([^/?]+)\?/) || (url || '').match(/\/([^/?]+)$/);
    return m ? m[1] : '';
  };

  const mark = (scroll) => {
    if (!main) return;
    const cur = file(main.getAttribute('src'));
    let active = null;
    thumbs.forEach((t) => {
      const on = !!cur && file(t.dataset.full) === cur;
      if (on) {
        t.setAttribute('aria-current', 'true');
        active = t;
      } else {
        t.removeAttribute('aria-current');
      }
    });
    track.classList.toggle('has-active', !!active);
    if (active && scroll) {
      const left = active.offsetLeft - (track.clientWidth - active.offsetWidth) / 2;
      track.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
    }
  };

  if (main) {
    new MutationObserver(() => mark(true)).observe(main, { attributes: true, attributeFilter: ['src'] });
  }
  thumbs.forEach((t) => t.addEventListener('click', () => setTimeout(() => mark(false), 0)));

  mark(false);
  updateArrows();
  // Images lazy-load and can change the scroll width after first paint.
  window.addEventListener('load', updateArrows);
})();

/**
 * Big-image controls: previous / next arrows, a keyboard left/right, and an
 * expand button that opens the same sequence full screen (where the "3 / 40"
 * counter lives; it is not drawn over the photo itself).
 * Steps through the thumbnail list (every gallery image), so it works the same
 * whichever way the picture was chosen.
 */
(function () {
  const main = document.querySelector('[data-cs-main-image]');
  const thumbs = Array.from(document.querySelectorAll('[data-cs-thumb]'));
  if (!main || thumbs.length < 2) return;
  const stage = main.parentElement;

  const svg = (d) =>
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;
  const LEFT = svg('<path d="m15 18-6-6 6-6"/>');
  const RIGHT = svg('<path d="m9 18 6-6-6-6"/>');
  const EXPAND = svg('<path d="M15 3h6v6"/><path d="M9 21H3v-6"/><path d="M21 3l-7 7"/><path d="M3 21l7-7"/>');
  const CLOSE = svg('<path d="M18 6 6 18"/><path d="m6 6 12 12"/>');

  const btn = (cls, html, label) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = cls;
    b.innerHTML = html;
    b.setAttribute('aria-label', label);
    return b;
  };

  const file = (url) => {
    const m = (url || '').match(/\/([^/?]+)\?/) || (url || '').match(/\/([^/?]+)$/);
    return m ? m[1] : '';
  };
  const index = () => {
    const cur = file(main.getAttribute('src'));
    const i = thumbs.findIndex((t) => file(t.dataset.full) === cur);
    return i < 0 ? 0 : i;
  };
  const go = (i) => {
    const n = (i + thumbs.length) % thumbs.length;
    main.src = thumbs[n].dataset.full;
  };

  const prev = btn('pdp-stage-btn pdp-stage-prev', LEFT, 'Previous image');
  const next = btn('pdp-stage-btn pdp-stage-next', RIGHT, 'Next image');
  const expand = btn('pdp-stage-btn pdp-stage-expand', EXPAND, 'View full screen');
  stage.append(prev, next, expand);

  prev.addEventListener('click', () => go(index() - 1));
  next.addEventListener('click', () => go(index() + 1));

  // Swipe on the picture (touch), without fighting vertical page scroll.
  let sx = 0, sy = 0;
  stage.addEventListener('touchstart', (e) => { sx = e.touches[0].clientX; sy = e.touches[0].clientY; }, { passive: true });
  stage.addEventListener('touchend', (e) => {
    const dx = e.changedTouches[0].clientX - sx;
    const dy = e.changedTouches[0].clientY - sy;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) go(index() + (dx < 0 ? 1 : -1));
  }, { passive: true });

  // Full-screen viewer.
  let box = null;
  const lbImg = document.createElement('img');
  lbImg.alt = '';
  const lbCount = document.createElement('div');
  lbCount.className = 'pdp-lightbox-count figures';
  const lbShow = () => {
    const i = index();
    lbImg.src = thumbs[i].dataset.full.replace(/width=\d+/, 'width=2048');
    lbCount.textContent = `${i + 1} / ${thumbs.length}`;
  };
  const step = (d) => { go(index() + d); lbShow(); };
  const close = () => {
    if (!box) return;
    box.remove();
    box = null;
    document.removeEventListener('keydown', onKey);
    document.documentElement.classList.remove('pdp-lightbox-open');
    expand.focus();
  };
  const onKey = (e) => {
    if (e.key === 'Escape') close();
    else if (e.key === 'ArrowLeft') step(-1);
    else if (e.key === 'ArrowRight') step(1);
  };
  const open = () => {
    box = document.createElement('div');
    box.className = 'pdp-lightbox';
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('aria-label', 'Product images');
    const x = btn('pdp-lightbox-btn pdp-lightbox-close', CLOSE, 'Close');
    const p = btn('pdp-lightbox-btn pdp-lightbox-prev', LEFT, 'Previous image');
    const n = btn('pdp-lightbox-btn pdp-lightbox-next', RIGHT, 'Next image');
    x.addEventListener('click', close);
    p.addEventListener('click', () => step(-1));
    n.addEventListener('click', () => step(1));
    box.addEventListener('click', (e) => { if (e.target === box) close(); });
    box.append(lbImg, x, p, n, lbCount);
    document.body.append(box);
    document.documentElement.classList.add('pdp-lightbox-open');
    document.addEventListener('keydown', onKey);
    lbShow();
    x.focus();
  };
  expand.addEventListener('click', open);
  main.addEventListener('click', open);
  main.style.cursor = 'zoom-in';
})();
